import { FC, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { Button } from 'components/ui/Button';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import clsx from 'clsx';

export interface ConfirmationOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  isLoading?: boolean;
  /**
   * Quand renseigné, l'utilisateur doit saisir exactement ce mot dans un champ
   * pour activer la confirmation (ex. "EFFACER" pour une suppression définitive).
   */
  requireTypedConfirmation?: string;
  /**
   * Masque le bouton "Annuler" pour transformer la modale en simple
   * notification informative à un bouton (ex. "Compris").
   */
  hideCancel?: boolean;
}

export const ConfirmationOverlay: FC<ConfirmationOverlayProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'COMMON.CONFIRM',
  cancelLabel = 'COMMON.CANCEL',
  variant = 'danger',
  isLoading = false,
  requireTypedConfirmation,
  hideCancel = false
}) => {
  const { t } = useTranslation(NsKey.Common);
  const [typedValue, setTypedValue] = useState('');

  // Réinitialise la saisie à chaque fermeture.
  useEffect(() => {
    if (!isOpen) setTypedValue('');
  }, [isOpen]);

  const typedConfirmationOk =
    !requireTypedConfirmation || typedValue === requireTypedConfirmation;

  const handleConfirm = useCallback(() => {
    if (!typedConfirmationOk) return;
    onConfirm();
    onClose();
  }, [onConfirm, onClose, typedConfirmationOk]);

  const getConfirmButtonColor = () => {
    switch (variant) {
      case 'danger':
        return 'red';
      case 'warning':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/40 transition duration-200 data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          transition
          className={clsx(
            'w-full max-w-sm rounded-xl bg-white p-6 shadow-xl',
            'transition duration-200 data-[closed]:scale-95 data-[closed]:opacity-0'
          )}
        >
          <h3 className="text-lg font-semibold text-gray-900">{t(title)}</h3>
          {description && (
            <p className="mt-2 text-sm text-gray-600">{t(description)}</p>
          )}

          {requireTypedConfirmation && (
            <input
              type="text"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={requireTypedConfirmation}
              autoFocus
              className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          )}

          <div className="mt-6 flex justify-end gap-3">
            {!hideCancel && (
              <Button
                outline
                disabled={isLoading}
                onClick={onClose}
              >
                {t(cancelLabel)}
              </Button>
            )}
            <Button
              color={getConfirmButtonColor()}
              disabled={isLoading || !typedConfirmationOk}
              onClick={handleConfirm}
            >
              {t(confirmLabel)}
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
